import React from 'react';
import { Link } from 'react-router-dom';
import { FaWaveSquare, FaCheckCircle, FaMusic, FaFlask, FaPaintBrush, FaPalette } from 'react-icons/fa';
import './Services.css';

const AcousticSolutions = () => {
  const features = [
    'Comprehensive acoustic analysis and measurement',
    'Room acoustics design and optimization',
    'Soundproofing and noise isolation',
    'Recording studio acoustic treatment',
    'Home theater acoustic design',
    'Office and conference room acoustics',
    'Noise control and vibration damping',
    'Custom acoustic panels and diffusers'
  ];

  const benefits = [
    {
      icon: <FaMusic />,
      title: 'Optimized Sound Quality',
      description: 'Transform your space with proper acoustic treatment that eliminates echoes, reduces reverberation, and enhances clarity.'
    },
    {
      icon: <FaFlask />,
      title: 'Scientific Approach',
      description: 'We use advanced measurement tools and acoustic modeling software to design solutions based on scientific principles.'
    },
    {
      icon: <FaPaintBrush />,
      title: 'Custom Solutions',
      description: 'Every space is unique. We create tailored acoustic treatments that address your specific challenges and requirements.'
    },
    {
      icon: <FaPalette />,
      title: 'Aesthetic Integration',
      description: 'Our acoustic solutions blend seamlessly with your interior design, combining functionality with visual appeal.'
    }
  ];

  return (
    <div className="service-page">
      {/* Hero Section */}
      <section className="service-hero">
        <div className="service-hero-overlay"></div>
        <div className="service-hero-content">
          <FaWaveSquare className="service-hero-icon" />
          <h1>Acoustic Solutions</h1>
          <p>
            Professional acoustic design, treatment, and soundproofing solutions for recording studios,
            home theaters, offices, auditoriums, and residential spaces. We combine science and art to
            create acoustically perfect environments for any application.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="service-content">
        <div className="container">
          <div className="content-grid">
            <div className="content-text">
              <h2>Professional Acoustic Engineering</h2>
              <p>
                Proper room acoustics are fundamental to achieving excellent sound quality, whether you're
                building a recording studio, home theater, conference room, or listening space. Our acoustic
                solutions address issues like unwanted reflections, standing waves, flutter echoes, and
                excessive reverberation that can muddy sound and reduce intelligibility.
              </p>
              <p>
                We provide comprehensive acoustic services including initial room analysis, computer modeling,
                treatment design, product specification, and installation supervision. Our solutions utilize
                a combination of absorption, diffusion, bass trapping, and isolation techniques to achieve
                optimal acoustic performance.
              </p>
              <p>
                From soundproofing studios and home theaters to improving speech clarity in conference rooms
                and controlling noise in open office environments, we have the expertise and products to solve
                any acoustic challenge you're facing.
              </p>
            </div>
            <div className="content-image">
              <img 
                src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&h=600&fit=crop&q=80" 
                alt="Acoustic Treatment" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="service-features">
        <div className="container">
          <h2>Our Acoustic Services</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div className="feature-item" key={index}>
                <FaCheckCircle />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="service-benefits">
        <div className="container">
          <div className="section-header">
            <h2>Why Choose Our Acoustic Solutions?</h2>
            <p>Scientific precision meets aesthetic excellence for perfect room acoustics</p>
          </div>
          <div className="values-grid">
            {benefits.map((benefit, index) => (
              <div className="value-card" key={index}>
                <div className="value-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="service-gallery">
        <div className="container">
          <h2>Our Acoustic Projects</h2>
          <div className="gallery-grid">
            <img src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=600&h=400&fit=crop&q=80" alt="Studio Acoustic Treatment" />
            <img src="https://images.unsplash.com/photo-1519508234439-4f23643125c1?w=600&h=400&fit=crop&q=80" alt="Acoustic Panels Installation" />
            <img src="https://images.unsplash.com/photo-1577750232530-ac1922088e1c?w=600&h=400&fit=crop&q=80" alt="Recording Studio Acoustics" />
            <img src="https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=600&h=400&fit=crop&q=80" alt="Home Theater Acoustic Design" />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-cta">
        <div className="container">
          <h2>Ready to Improve Your Room Acoustics?</h2>
          <p>Contact us today for a professional acoustic consultation</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary">
              Get a Consultation
            </Link>
            <a href="tel:+919876543210" className="btn btn-secondary">
              Call Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AcousticSolutions;
